/** One column of a CSV export: the header text and how to read it from a row. */
export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string;
}

/** Byte-order mark, so Excel reads the file as UTF-8 and keeps the accents. */
const UTF8_BOM = "﻿";

/** Quotes a value for CSV: doubles the quotes and always wraps, so separators and line breaks are safe. */
const escapeCell = (value: string) => {
  const escaped = (value ?? "").replace(/"/g, "\"\"");

  return `"${escaped}"`;
};

/**
 * Builds a CSV from rows and hands it to the browser as a download.
 *
 * Uses the semicolon separator because the audience opens these in Excel under
 * a pt-BR locale, where a comma-separated file lands in a single column.
 */
export const downloadCsv = <T>(fileName: string, columns: CsvColumn<T>[], rows: T[]): void => {
  const lines = [
    columns.map((column) => escapeCell(column.header)).join(";"),
    ...rows.map((row) => columns.map((column) => escapeCell(column.value(row))).join(";"))
  ];

  const blob = new Blob([UTF8_BOM + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Turns a title into a safe file-name fragment: no accents, no separators. */
export const toFileNameSlug = (value: string): string => value
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .replace(/[^a-zA-Z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .toLowerCase()
  .slice(0, 60);
